import type { SupabaseClient } from '@supabase/supabase-js';
import { renderMappingTab } from '../tabs/kaufland-list-mapping.js';
import { renderPreviewTab } from '../tabs/kaufland-list-preview.js';
import { renderImagesTab } from '../tabs/kaufland-list-images.js';
import { renderVariantsTab } from '../tabs/kaufland-list-variants.js';
import { renderOptionalFieldsTab } from '../tabs/optional-fields.js';
import { showToast } from '@vario-connect/frontend-design';

/** Zweistufige Bestätigung (Projektregel: keine window.confirm/alert).
 *  Erster Klick „schärft" den Button (Label-Wechsel + danger-Optik), nach 4s
 *  ohne zweiten Klick fällt er zurück. Zweiter Klick führt onConfirm aus. */
export function wireTwoStepConfirm(
  btn: HTMLButtonElement,
  armedHtml: string,
  onConfirm: () => void,
): void {
  const idleHtml = btn.innerHTML;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const disarm = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    btn.dataset['armed'] = '';
    btn.innerHTML = idleHtml;
    btn.classList.remove('klst-btn-armed');
  };
  btn.addEventListener('click', () => {
    if (btn.dataset['armed'] === '1') { disarm(); onConfirm(); return; }
    btn.dataset['armed'] = '1';
    btn.innerHTML = armedHtml;
    btn.classList.add('klst-btn-armed');
    timer = setTimeout(disarm, 4000);
  });
}

// ============================================================================
// Geteiltes Listing-Detail-Pane — wird vom Lister (Master-Detail, rechte
// Spalte) UND vom Vario-Slot listing-status (Configure-Modus im Artikel)
// gerendert. Extrahiert aus kaufland-list.ts (2026-06-03), Verhalten 1:1.
//
// Host-Vertrag:
//   * ctx liefert Supabase-Client + Session + supabaseUrl (beide Hosts haben das).
//   * paneHost.kind steuert NICHTS funktional — beide Orte verhalten sich gleich;
//     das Feld existiert für CSS-/Telemetrie-Zwecke und künftige Abweichungen.
//   * onListingChanged(): Status/Inhalt des Listings hat sich geändert (Publish
//     enqueued, archiviert, Modus gewechselt) — Lister refresht die Master-Liste,
//     Slot refresht seinen Inbox-Status.
//   * onDeleted(): Listing wurde gelöscht — Lister leert das Detail + reloadet,
//     Slot fällt zurück in den Kategorie-Choice-Modus.
// ============================================================================

export interface DetailPaneCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

export interface DetailPaneHost {
  kind: 'lister' | 'slot';
  onListingChanged(): void;
  onDeleted(): void;
}

// ============================================================================
// Typen + Konstanten (1:1 aus kaufland-list.ts übernommen)
// ============================================================================

type DetailTab = 'overview' | 'variants' | 'images' | 'mapping' | 'optional' | 'preview';

export const STOREFRONT_LABELS: Record<string, string> = {
  de: 'Deutschland',
  at: 'Österreich',
  cz: 'Tschechien',
  sk: 'Slowakei',
  pl: 'Polen',
  fr: 'Frankreich',
  it: 'Italien',
};

export interface ListingDetail {
  id: string;
  vario_article_id: string;
  vario_listing_id: string;
  vario_article_number: string | null;
  vario_article_name: string | null;
  storefront: string;
  vario_category_id: string | null;
  vario_category_name: string | null;
  kaufland_category_id: number | null;
  vario_listing_snapshot: Record<string, unknown> | null;
  is_variant_parent: boolean;
  mapping_override: Record<string, unknown> | null;
  status: ListingStatus;
  last_error: string | null;
  kaufland_product_id: string | null;
  kaufland_unit_id: string | null;
  kaufland_offer_id: string | null;
  first_seen_at: string;
  last_scan_at: string;
  updated_at: string;
  /** Aufgehydratet via separates select auf kaufland.listings (kaufland_get_listing_detail
   *  liefert die Spalten nicht). Fallback 'new'. */
  mode?: 'new' | 'attach';
  attach_id_product?: number | null;
}

interface OfferDefaults {
  condition: string | null;
  handling_time: number | null;
  id_shipping_group: string | null;
  vat_indicator: string | null;
  field_mappings?: Record<string, unknown> | null;
}

interface ShippingGroupRow {
  id_shipping_group: string;
  name: string | null;
}

interface VatIndicatorRow {
  vat_indicator: string;
  label: string | null;
  vat_rate: number | null;
}

export type ListingStatus =
  | 'pending'
  | 'mapping'
  | 'mapped'
  | 'queued'
  | 'published'
  | 'error'
  | 'archived';

export const STATUS_PILL: Record<ListingStatus, { cls: string; label: string }> = {
  pending:   { cls: 'v-pill--info',  label: 'Neu' },
  mapping:   { cls: 'v-pill--warn',  label: 'In Bearbeitung' },
  mapped:    { cls: 'v-pill--ok',    label: 'Bereit' },
  queued:    { cls: 'v-pill--info',  label: 'Wird gelistet' },
  published: { cls: 'v-pill--ok',    label: 'Gelistet' },
  error:     { cls: 'v-pill--error', label: 'Fehler' },
  archived:  { cls: 'v-pill--muted', label: 'Archiviert' },
};

// ============================================================================
// Vario-Base-URL — Modul-Level-Memo
// ============================================================================

// Vario-Base-URL einmal pro Page-Load ziehen (RPC dekodiert offline-token-iss
// serverseitig). Modul-Level-Memo: Session/Tenant sind pro iframe-Load fix.
let varioBaseUrlPromise: Promise<string | null> | null = null;
function getVarioBaseUrl(supabase: SupabaseClient): Promise<string | null> {
  return (varioBaseUrlPromise ??= Promise.resolve(
    supabase.rpc('kaufland_get_vario_base_url').then(({ data, error }) => {
      if (error) { console.warn('[listing-detail-pane] vario base url failed', error); return null; }
      return typeof data === 'string' && data.length > 0 ? data : null;
    }),
  ));
}

// ============================================================================
// Entry-Funktion
// ============================================================================

export async function renderListingDetailPane(
  mount: HTMLElement,
  ctx: DetailPaneCtx,
  listingId: string,
  paneHost: DetailPaneHost,
): Promise<void> {
  mount.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Details…</div>`;
  const { data, error } = await ctx.supabase.rpc('kaufland_get_listing_detail', { _id: listingId });
  if (error) {
    mount.innerHTML = `<div class="klst-loading klst-loading--error">Fehler: ${escapeHtml(error.message)}</div>`;
    return;
  }
  const detail = (Array.isArray(data) ? data[0] : data) as ListingDetail | null | undefined;
  if (!detail) {
    mount.innerHTML = `<div class="klst-loading">Listing nicht gefunden.</div>`;
    return;
  }

  // Hydrate mode + attach_id_product — kaufland_get_listing_detail liefert die
  // Spalten nicht. Sequentieller select, Fehler ist fail-soft (fallback 'new').
  const { data: modeRow, error: modeErr } = await ctx.supabase
    .schema('kaufland')
    .from('listings')
    .select('mode, attach_id_product')
    .eq('id', detail.id)
    .maybeSingle();
  if (modeErr) {
    console.warn('[listing-detail-pane] detail mode hydration failed:', modeErr.message);
  } else if (modeRow) {
    const row = modeRow as { mode?: string | null; attach_id_product?: number | null };
    detail.mode = row.mode === 'attach' ? 'attach' : 'new';
    detail.attach_id_product = row.attach_id_product ?? null;
  }

  // Render-Verzweigung: mode='attach' rendert den reduzierten Aufschalten-
  // Single-Panel statt Mapping/Bilder/Preview-Tabs.
  if (detail.mode === 'attach') {
    await renderAttachPanel(mount, ctx, detail, () => paneHost.onListingChanged());
    return;
  }

  mount.innerHTML = renderDetail(detail);
  wireDetailTabs(mount, ctx, detail);

  const archiveBtn = mount.querySelector<HTMLButtonElement>('#klst-detail-archive');
  archiveBtn?.addEventListener('click', () => void archiveListing(detail.id, detail.status !== 'archived'));
  const deleteBtn = mount.querySelector<HTMLButtonElement>('#klst-detail-delete');
  if (deleteBtn) {
    wireTwoStepConfirm(
      deleteBtn,
      `<span class="v-icon fa-regular fa-trash"></span> Wirklich löschen?`,
      () => void deleteListing(detail),
    );
  }

  // Artikel-oeffnen-Icon-Link in der Tab-Bar (rechts) — async populiert wenn die
  // tenant-base-URL geladen ist. Bis dahin ist der Link versteckt.
  const articleLink = mount.querySelector<HTMLAnchorElement>('#klst-detail-vario-link');
  if (articleLink) {
    void getVarioBaseUrl(ctx.supabase).then((baseUrl) => {
      if (!baseUrl) return;
      articleLink.href = `${baseUrl}/products/articles/${encodeURIComponent(detail.vario_article_id)}/detail`;
      articleLink.style.display = '';
    });
  }

  async function archiveListing(id: string, archive: boolean) {
    const { error: archErr } = await ctx.supabase.rpc('kaufland_archive_listing', { _id: id, _archive: archive });
    if (archErr) {
      showToast(`Archivieren fehlgeschlagen: ${archErr.message}`, 'error');
      return;
    }
    paneHost.onListingChanged();
  }

  async function deleteListing(d: ListingDetail) {
    const { error: delErr } = await ctx.supabase.rpc('kaufland_delete_listing', { _id: d.id });
    if (delErr) {
      showToast(`Löschen fehlgeschlagen: ${delErr.message}`, 'error');
      return;
    }
    showToast('Listing gelöscht.', 'success');
    paneHost.onDeleted();
  }
}

// ============================================================================
// wireDetailTabs (aus kaufland-list.ts Z. 468–564, KauflandListCtx → DetailPaneCtx)
// ============================================================================

function wireDetailTabs(mount: HTMLElement, ctx: DetailPaneCtx, detail: ListingDetail): void {
  const tabsBar = mount.querySelector<HTMLElement>('#klst-detail-tabs');
  const panes = mount.querySelectorAll<HTMLElement>('[data-detail-pane]');
  if (!tabsBar) return;

  const switchTo = (target: DetailTab) => {
    tabsBar.querySelectorAll<HTMLButtonElement>('button[data-detail-tab]').forEach((b) => {
      b.classList.toggle('klst-tab--active', b.dataset['detailTab'] === target);
    });
    panes.forEach((p) => { p.hidden = p.dataset['detailPane'] !== target; });

    if (target === 'variants') {
      const varHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="variants"]');
      if (varHost) {
        void renderVariantsTab(varHost, {
          supabase: ctx.supabase,
          supabaseUrl: ctx.supabaseUrl,
          session: { token: ctx.session.token },
        }, {
          storefront: detail.storefront,
          kaufland_category_id: detail.kaufland_category_id,
          vario_category_id: detail.vario_category_id,
          vario_listing_id: detail.vario_listing_id,
          vario_article_id: detail.vario_article_id,
        });
      }
    }
    if (target === 'images') {
      const imgHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="images"]');
      if (imgHost) {
        void renderImagesTab(imgHost, {
          supabase: ctx.supabase,
          supabaseUrl: ctx.supabaseUrl,
          session: { token: ctx.session.token },
        }, {
          vario_article_id: detail.vario_article_id,
        });
      }
    }
    if (target === 'mapping') {
      const mapHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="mapping"]');
      if (mapHost && !mapHost.dataset['loaded']) {
        mapHost.dataset['loaded'] = '1';
        void renderMappingTab(mapHost, {
          supabase: ctx.supabase,
          supabaseUrl: ctx.supabaseUrl,
          session: { token: ctx.session.token },
          onPreviewRequested: () => switchTo('preview'),
        }, {
          storefront: detail.storefront,
          vario_category_id: detail.vario_category_id,
          kaufland_category_id: detail.kaufland_category_id,
          vario_listing_id: detail.vario_listing_id,
          vario_article_id: detail.vario_article_id,
        });
      }
    }
    if (target === 'optional') {
      const optHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="optional"]');
      if (optHost) {
        // Bei JEDEM Klick neu rendern — Default/Override koennen sich aendern, frische
        // Werte sind hier wichtiger als Caching (analog Preview).
        void renderOptionalFieldsTab(optHost, {
          supabase: ctx.supabase,
          supabaseUrl: ctx.supabaseUrl,
          session: { token: ctx.session.token },
        }, {
          storefront: detail.storefront,
          vario_article_id: detail.vario_article_id,
          kaufland_category_id: detail.kaufland_category_id,
        });
      }
    }
    if (target === 'preview') {
      const prevHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="preview"]');
      if (prevHost) {
        // Preview wird bei JEDEM Klick neu gerendert — Mapping kann sich zwischen den
        // Tab-Wechseln im Mapping-Tab veraendert haben, frische Daten sind hier wichtiger
        // als Caching.
        void renderPreviewTab(prevHost, {
          supabase: ctx.supabase,
          supabaseUrl: ctx.supabaseUrl,
          session: { token: ctx.session.token },
        }, {
          storefront: detail.storefront,
          kaufland_category_id: detail.kaufland_category_id,
          vario_listing_id: detail.vario_listing_id,
          vario_article_id: detail.vario_article_id,
        });
      }
    }
  };

  tabsBar.querySelectorAll<HTMLButtonElement>('button[data-detail-tab]').forEach((btn) => {
    btn.addEventListener('click', () => switchTo(btn.dataset['detailTab'] as DetailTab));
  });
}

// ============================================================================
// Render-Helper (aus kaufland-list.ts Z. 750–850, 895–912)
// ============================================================================

function renderDetail(d: ListingDetail): string {
  const snapshot = d.vario_listing_snapshot ?? {};
  const description = stringField(snapshot, 'description');
  const ean = stringField(snapshot, 'ean');
  const archiveLabel = d.status === 'archived' ? 'Wiederaufnehmen' : 'Archivieren';
  return `
    <div>
      <!-- Tab bar -->
      <div id="klst-detail-tabs" class="klst-tabs">
        <button type="button" class="klst-tab klst-tab--active" data-detail-tab="overview">
          <span class="v-icon fa-regular fa-circle-info"></span> Übersicht
        </button>
        ${d.is_variant_parent ? `
        <button type="button" class="klst-tab" data-detail-tab="variants">
          <span class="v-icon fa-regular fa-list-check"></span> Varianten
        </button>` : `
        <button type="button" class="klst-tab" data-detail-tab="mapping">
          <span class="v-icon fa-regular fa-sliders"></span> Mapping
        </button>
        <button type="button" class="klst-tab" data-detail-tab="optional">
          <span class="v-icon fa-regular fa-list-check"></span> Optionale Felder
        </button>
        <button type="button" class="klst-tab" data-detail-tab="images">
          <span class="v-icon fa-regular fa-images"></span> Bilder
        </button>
        <button type="button" class="klst-tab" data-detail-tab="preview">
          <span class="v-icon fa-regular fa-eye"></span> Vorschau
        </button>`}
        <a id="klst-detail-vario-link" href="#" target="_blank" rel="noopener noreferrer"
           class="klst-tabs-vario-link"
           title="Artikel in Vario öffnen — fehlende Werte direkt am Artikel pflegen"
           style="display:none;">
          <span class="v-icon fa-regular fa-arrow-up-right-from-square"></span>
        </a>
      </div>

      <!-- Pane: Übersicht -->
      <div data-detail-pane="overview">
        <div class="v-pa-md">
          <div class="v-row v-row-sm klst-detail-actions">
            <div class="v-col-shrink">
              <button type="button" class="v-button" id="klst-detail-archive">
                <span class="v-icon fa-regular fa-box-archive"></span>
                ${escapeHtml(archiveLabel)}
              </button>
            </div>
            ${d.status === 'queued' || d.status === 'published' ? '' : `
              <div class="v-col-shrink">
                <button type="button" class="v-button v-button--ghost klst-detail-delete-btn" id="klst-detail-delete" title="Listing aus der Inbox loeschen — Status ${escapeHtml(d.status)} ist sicher loeschbar">
                  <span class="v-icon fa-regular fa-trash"></span>
                  <span>Löschen</span>
                </button>
              </div>`}
          </div>

          ${d.last_error ? `
            <div class="v-banner v-banner-danger klst-detail-error">
              <span class="v-icon fa-light fa-circle-xmark"></span>
              <div><strong>Letzter Fehler:</strong> ${escapeHtml(d.last_error)}</div>
            </div>` : ''}

          <h4 class="klst-detail-section">Daten aus Vario</h4>
          <table class="klst-detail-table">
            <tbody>
              <tr><th>Vario-Artikel-ID</th><td>${escapeHtml(d.vario_article_id)}</td></tr>
              <tr><th>Vario-Listing-ID</th><td>${escapeHtml(d.vario_listing_id)}</td></tr>
              <tr><th>Vario-Kategorie</th><td>${escapeHtml(d.vario_category_name ?? d.vario_category_id ?? '—')}</td></tr>
              <tr><th>EAN</th><td>${escapeHtml(ean ?? '—')}</td></tr>
              <tr><th>Beschreibung</th><td>${escapeHtml(description ?? '—')}</td></tr>
              <tr><th>Variantenvater</th><td>${d.is_variant_parent ? 'Ja' : 'Nein'}</td></tr>
            </tbody>
          </table>

          <h4 class="klst-detail-section">Kaufland</h4>
          <table class="klst-detail-table">
            <tbody>
              <tr><th>Kaufland-Kategorie-ID</th><td>${d.kaufland_category_id ?? '— (wird beim Push gemappt)'}</td></tr>
              <tr><th>Kaufland-Produkt</th><td>${escapeHtml(d.kaufland_product_id ?? '—')}</td></tr>
              <tr><th>Kaufland-Unit</th><td>${escapeHtml(d.kaufland_unit_id ?? '—')}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pane: Varianten (lazy-rendered beim Tab-Klick) -->
      <div data-detail-pane="variants" hidden></div>

      <!-- Pane: Bilder (lazy-rendered beim Tab-Klick) -->
      <div data-detail-pane="images" hidden></div>

      <!-- Pane: Mapping -->
      <div data-detail-pane="mapping" hidden></div>

      <!-- Pane: Optionale Felder (lazy-rendered beim Tab-Klick) -->
      <div data-detail-pane="optional" hidden></div>

      <!-- Pane: Preview (lazy-rendered beim Tab-Klick) -->
      <div data-detail-pane="preview" hidden></div>
    </div>
  `;
}

function stringField(obj: Record<string, unknown>, key: string): string | null {
  const v = obj[key];
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
}

function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

// ============================================================================
// Aufschalten-Single-Panel — mode='attach'
// ----------------------------------------------------------------------------
// Wird statt der Mapping/Bilder/Preview-Tabs gerendert, wenn das Listing im
// attach-Modus ist (id_product ist aus EAN-Lookup bekannt). Reduzierter Flow:
//   1. Katalog-Karte (read-only Header) — was wir aus snapshot+DB kennen
//   2. Offer-Form (8 Felder) — vorbefuellt aus kaufland_resolve_offer_defaults
//      + Vario-Live-Werte (listing_price/amount/minimum_price) via field_mappings
//   3. Footer mit Primary "Aufschalten" + Sekundaer "Doch neu listen"
// Submit persistiert in vario_listing_snapshot.offerOverrides (snake-case keys,
// strikt synchron mit Worker T7) und enqueued via kaufland_enqueue_listing_publish.
// ============================================================================

// ---- Vario-Mapping-Sources: callFn-Helper + Resolver --------------------------
// Spiegelt das Pattern aus kaufland-list-mapping.ts. Wir koennten den Helper auch
// hochziehen, aber dann muesste ein gemeinsames ctx-Interface her — dafuer sind
// die paar Zeilen Duplikat nicht teuer genug.

async function callMappingSourcesFn<T>(
  ctx: DetailPaneCtx,
  body: unknown,
): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/vario-mapping-sources`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${ctx.session.token}`,
      },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown;
    try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
    if (!res.ok) {
      const errBag = (parsed as { error?: { code?: string; message?: string } } | null)?.error;
      return { ok: false, error: errBag ? `${errBag.code ?? '?'}: ${errBag.message ?? '?'}` : `HTTP ${res.status}` };
    }
    return { ok: true, value: parsed as T };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

interface MappingSourcesResp {
  groups: Array<{
    key: string;
    label: string;
    sources: Array<{
      sourceKey: string;
      path: string;
      dataType: string;
      label: string;
      exampleValue?: unknown;
    }>;
  }>;
  meta?: {
    autoPriceValueCents?: number | null;
    autoStockValue?: number | null;
    autoVatIndicator?: string | null;
    autoVatRate?: number | null;
    [k: string]: unknown;
  };
}

interface ResolvedVarioValues {
  /** Listing-Preis in EUR (decimal) — fuer das EUR-Input direkt einsetzbar. */
  listingPriceEur: number | null;
  /** Lagerbestand (integer). */
  amount: number | null;
  /** Mindestpreis in EUR (decimal). */
  minimumPriceEur: number | null;
  /** Von der Vario-Cascade aufgeloester MwSt-Indikator (article.taxSchemaRef →
   *  tax-rates → kaufland.vat_indicators-Match). null wenn nicht aufloesbar. */
  vatIndicator: string | null;
  /** MwSt-Rate als Prozentzahl (z.B. 19) — nur zur Anzeige im Hint. */
  vatRate: number | null;
  /** Hint-Strings pro Feld (welcher Vario-Pfad genutzt wurde). */
  hints: Partial<Record<'listing_price' | 'amount' | 'minimum_price', string>>;
}

/** Liest die formatierten `exampleValue`-Strings aus vario-mapping-sources und
 *  versucht eine numerische Interpretation:
 *   - Auto-Sources (auto.listing_price, auto.stock_available) liefern formatierten
 *     String → wir greifen stattdessen direkt auf den Raw-Wert in `meta`.
 *   - Standard-Listing/Article-Sources wie `grossSalesPrice`, `stockAvailable`:
 *     der String kommt aus `String(number)` (siehe truncateForOption) und ist als
 *     EUR/Integer parsebar.
 *  Liefert null wenn weder Match noch parsebar.
 *
 *  Konvertierung: Preise im Vario-Modell sind EUR-Decimals (number) → bleiben EUR;
 *  Auto-Source liefert Cents → wir teilen durch 100. */
async function resolveVarioOfferValues(
  ctx: DetailPaneCtx,
  storefront: string,
  articleId: string,
  listingId: string | null,
  fieldMappings: Record<string, { source?: string; path?: string } | undefined>,
): Promise<ResolvedVarioValues> {
  const empty: ResolvedVarioValues = {
    listingPriceEur: null, amount: null, minimumPriceEur: null,
    vatIndicator: null, vatRate: null, hints: {},
  };
  const res = await callMappingSourcesFn<MappingSourcesResp>(ctx, {
    action: 'list',
    storefront,
    articleId,
    ...(listingId ? { listingId } : {}),
  });
  if (!res.ok) {
    console.warn('[listing-detail-pane] vario-mapping-sources for attach pre-fill failed:', res.error);
    return empty;
  }

  // Index alle Sources nach path UND nach sourceKey (damit wir bei matching gegen
  // den vom User in field_mappings.path eingetragenen Wert flexibel sind).
  const byPath = new Map<string, { sourceKey: string; exampleValue?: unknown }>();
  for (const g of res.value.groups ?? []) {
    for (const s of g.sources ?? []) {
      if (s.path) byPath.set(s.path, { sourceKey: s.sourceKey, exampleValue: s.exampleValue });
    }
  }

  const meta = res.value.meta ?? {};
  const autoPriceCents = typeof meta.autoPriceValueCents === 'number' ? meta.autoPriceValueCents : null;
  const autoStock = typeof meta.autoStockValue === 'number' ? meta.autoStockValue : null;

  // Strings vom Format "12.34" (Article/Listing) ODER "12,34 € (aus Stammdaten)"
  // (Auto-Source) ODER "5 verfügbar" parsen.
  const parseLeadingNumber = (v: unknown): number | null => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v !== 'string') return null;
    // Erstes Zahlen-Token greifen (de-DE: Komma als Dezimaltrenner moeglich).
    const m = v.match(/-?\d+(?:[.,]\d+)?/);
    if (!m) return null;
    const n = Number(m[0].replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  const resolveOne = (
    fieldKey: 'listing_price' | 'amount' | 'minimum_price',
  ): { value: number | null; hint?: string } => {
    const mapping = fieldMappings[fieldKey];
    const path = typeof mapping?.path === 'string' && mapping.path.length > 0 ? mapping.path : null;
    if (path) {
      const src = byPath.get(path);
      if (src) {
        // Auto-Sources: Raw aus meta bevorzugen (exampleValue ist formatiert).
        if (src.sourceKey === 'auto.listing_price' && autoPriceCents !== null) {
          return { value: autoPriceCents / 100, hint: path };
        }
        if (src.sourceKey === 'auto.stock_available' && autoStock !== null) {
          return { value: autoStock, hint: path };
        }
        const parsed = parseLeadingNumber(src.exampleValue);
        if (parsed !== null) return { value: parsed, hint: path };
      }
    }
    // Path nicht konfiguriert ODER nicht aufloesbar → Auto-Fallback (best effort).
    if (fieldKey === 'listing_price' && autoPriceCents !== null) {
      return { value: autoPriceCents / 100, hint: 'auto.listing_price' };
    }
    if (fieldKey === 'amount' && autoStock !== null) {
      return { value: autoStock, hint: 'auto.stock_available' };
    }
    return { value: null };
  };

  const lp = resolveOne('listing_price');
  const am = resolveOne('amount');
  const mp = resolveOne('minimum_price');
  const autoVatIndicator = typeof meta.autoVatIndicator === 'string' && meta.autoVatIndicator.length > 0
    ? meta.autoVatIndicator : null;
  const autoVatRate = typeof meta.autoVatRate === 'number' ? meta.autoVatRate : null;
  return {
    listingPriceEur: lp.value,
    amount: am.value !== null ? Math.floor(am.value) : null,
    minimumPriceEur: mp.value,
    vatIndicator: autoVatIndicator,
    vatRate: autoVatRate,
    hints: {
      ...(lp.hint ? { listing_price: lp.hint } : {}),
      ...(am.hint ? { amount: am.hint } : {}),
      ...(mp.hint ? { minimum_price: mp.hint } : {}),
    },
  };
}

async function renderAttachPanel(
  host: HTMLElement,
  ctx: DetailPaneCtx,
  detail: ListingDetail,
  onReload: () => void,
): Promise<void> {
  host.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Aufschalt-Daten…</div>`;

  // Parallel: Defaults, Shipping-Groups, VAT-Indicators, Category-Label
  const categoryPromise: Promise<{ data: { name: string | null } | null; error: { message: string } | null }> =
    detail.kaufland_category_id != null
      ? (ctx.supabase.schema('kaufland').from('categories')
          .select('name')
          .eq('storefront', detail.storefront)
          .eq('category_id', detail.kaufland_category_id)
          .maybeSingle() as unknown as Promise<{ data: { name: string | null } | null; error: { message: string } | null }>)
      : Promise.resolve({ data: null, error: null });

  const [defaultsRes, shippingRes, vatRes, categoryRes] = await Promise.all([
    ctx.supabase.rpc('kaufland_resolve_offer_defaults', { _listing_id: detail.id }),
    ctx.supabase.schema('kaufland').from('shipping_groups')
      .select('id_shipping_group, name')
      .eq('storefront', detail.storefront)
      .order('name'),
    ctx.supabase.schema('kaufland').from('vat_indicators')
      .select('vat_indicator, label, vat_rate')
      .eq('storefront', detail.storefront)
      .order('vat_indicator'),
    categoryPromise,
  ]);

  if (defaultsRes.error) {
    host.innerHTML = `<div class="klst-loading klst-loading--error">Defaults laden fehlgeschlagen: ${escapeHtml(defaultsRes.error.message)}</div>`;
    return;
  }
  const defaults = (defaultsRes.data ?? {}) as OfferDefaults;
  const shippingGroups = (shippingRes.data ?? []) as ShippingGroupRow[];
  const vatIndicators = (vatRes.data ?? []) as VatIndicatorRow[];
  const categoryLabel = categoryRes.data?.name ?? null;

  // Vario-Live-Werte (Preis/Bestand/MinPreis) auf Basis der field_mappings aus den
  // Defaults resolven. Laeuft erst NACH defaults, weil wir field_mappings brauchen
  // — und ist optional: Vario-Hickup fuehrt zu empty-Prefill, der User editiert
  // dann manuell.
  const fieldMappings = (defaults.field_mappings ?? {}) as Record<string, { source?: string; path?: string } | undefined>;
  const varioResolved = await resolveVarioOfferValues(
    ctx,
    detail.storefront,
    detail.vario_article_id,
    detail.vario_listing_id ?? null,
    fieldMappings,
  );

  // Bestehende Overrides aus dem Snapshot vorbefuellen, falls der User schon
  // mal gespeichert hatte (nicht-published Status ist editierbar).
  const snapshot = (detail.vario_listing_snapshot ?? {}) as Record<string, unknown>;
  const existingOverrides = (snapshot['offerOverrides'] ?? {}) as Record<string, unknown>;
  const ean = stringField(snapshot, 'ean');

  const conditionOptions: Array<{ value: string; label: string }> = [
    { value: 'NEW',          label: 'Neu' },
    { value: 'LIKE_NEW',     label: 'Wie neu' },
    { value: 'VERY_GOOD',    label: 'Sehr gut' },
    { value: 'GOOD',         label: 'Gut' },
    { value: 'ACCEPTABLE',   label: 'Akzeptabel' },
    { value: 'REFURBISHED',  label: 'Refurbished' },
  ];

  const conditionValue = pickStringOverride(existingOverrides, 'condition') ?? defaults.condition ?? 'NEW';
  const handlingTimeValue = pickNumberOverride(existingOverrides, 'handling_time') ?? defaults.handling_time ?? 2;
  const shippingValue = pickStringOverride(existingOverrides, 'id_shipping_group') ?? defaults.id_shipping_group ?? '';
  // VAT-Default-Aufloesung (Reihenfolge):
  //   1. Override aus snapshot.offerOverrides (User hat explizit gepflegt)
  //   2. Profil-Default aus kaufland.offer_default_profiles
  //   3. Vario-Cascade: article.taxSchemaRef -> tax-rates -> kaufland.vat_indicators
  //      (identisches Schema wie der regulaere Lister via vario-mapping-sources)
  //   4. leer lassen — User muss waehlen
  let vatValue = pickStringOverride(existingOverrides, 'vat_indicator') ?? defaults.vat_indicator ?? '';
  let vatAutoSource: 'vario' | null = null;
  if (!vatValue && varioResolved.vatIndicator) {
    // Nur akzeptieren wenn der von Vario aufgeloeste Code in der Storefront-Liste existiert.
    const exists = vatIndicators.some((v) => v.vat_indicator === varioResolved.vatIndicator);
    if (exists) { vatValue = varioResolved.vatIndicator; vatAutoSource = 'vario'; }
  }
  const vatAutoChosen = vatAutoSource !== null;
  const listingPriceCents = pickNumberOverride(existingOverrides, 'listing_price');
  const amountValue = pickNumberOverride(existingOverrides, 'amount');
  const minimumPriceCents = pickNumberOverride(existingOverrides, 'minimum_price');
  const noteValue = pickStringOverride(existingOverrides, 'note') ?? '';

  // Pre-Fill-Prioritaet (hoechste zuerst):
  //   1. existing offerOverrides (User-Override aus Snapshot, Cents → /100)
  //   2. varioResolved (frisch via vario-mapping-sources gezogen, EUR / Integer)
  //   3. leer (User muss von Hand)
  const listingPriceEuro = listingPriceCents != null
    ? (listingPriceCents / 100).toFixed(2)
    : (varioResolved.listingPriceEur != null ? varioResolved.listingPriceEur.toFixed(2) : '');
  const minimumPriceEuro = minimumPriceCents != null
    ? (minimumPriceCents / 100).toFixed(2)
    : (varioResolved.minimumPriceEur != null ? varioResolved.minimumPriceEur.toFixed(2) : '');
  const amountPrefill = amountValue != null
    ? String(amountValue)
    : (varioResolved.amount != null ? String(varioResolved.amount) : '');

  // UI-Hint: zeigt pro Feld an, wenn der Wert frisch aus Vario gezogen wurde (und
  // nicht aus einem bestehenden Override). Macht dem User klar, woher der
  // Vorbefuellwert kommt.
  const hintListingPrice = listingPriceCents == null && varioResolved.hints.listing_price
    ? varioResolved.hints.listing_price : null;
  const hintAmount = amountValue == null && varioResolved.hints.amount
    ? varioResolved.hints.amount : null;
  const hintMinimumPrice = minimumPriceCents == null && varioResolved.hints.minimum_price
    ? varioResolved.hints.minimum_price : null;

  const sf = (STOREFRONT_LABELS[detail.storefront] ?? detail.storefront).toUpperCase();
  const statusPill = STATUS_PILL[detail.status] ?? STATUS_PILL.pending;
  const isReadonly = detail.status === 'queued' || detail.status === 'published';

  // ---- Render ---------------------------------------------------------------
  host.innerHTML = `
    <div class="klst-attach v-pa-md">
      <div class="klst-attach-header">
        <div class="klst-attach-header-title">
          <span class="v-icon fa-regular fa-link"></span>
          Aufschalten auf Kaufland-Katalog-Eintrag
        </div>
        <div class="klst-attach-header-pills">
          <span class="v-pill v-pill--info">Aufschalten</span>
          <span class="v-pill ${statusPill.cls}">${escapeHtml(statusPill.label)}</span>
          <span class="v-pill v-pill--muted">${escapeHtml(sf)}</span>
          <a id="klst-attach-vario-link" href="#" target="_blank" rel="noopener noreferrer"
             class="klst-tabs-vario-link"
             title="Artikel in Vario öffnen — fehlende Werte direkt am Artikel pflegen"
             style="display:none;">
            <span class="v-icon fa-regular fa-arrow-up-right-from-square"></span>
          </a>
        </div>
      </div>

      <div class="v-banner v-banner-info klst-attach-info">
        <span class="v-icon fa-light fa-circle-info"></span>
        <div>Dieses Angebot existiert bereits bei Kaufland. Beim Aufschalten werden nur Deine Verkaufsbedingungen ergaenzt — Produkt-Stammdaten, Bilder und Beschreibung kommen aus dem Katalog-Eintrag.</div>
      </div>

      <h4 class="klst-detail-section">Katalog-Eintrag</h4>
      <table class="klst-detail-table">
        <tbody>
          <tr><th>Artikel (Vario)</th><td>${escapeHtml(detail.vario_article_name ?? detail.vario_article_id)}</td></tr>
          <tr><th>SKU (id_offer)</th><td>${
            detail.vario_article_number
              ? `<a id="klst-attach-sku-link" href="#" target="_blank" rel="noopener noreferrer"
                    class="klst-attach-sku-link" style="display:none;"
                    title="Artikel in Vario öffnen"><code>${escapeHtml(detail.vario_article_number)}</code>
                    <span class="v-icon fa-regular fa-arrow-up-right-from-square"></span></a>
                  <code id="klst-attach-sku-text">${escapeHtml(detail.vario_article_number)}</code>`
              : '<span class="v-text-muted">— wird beim ersten Push aus Vario geladen</span>'
          }</td></tr>
          <tr><th>EAN</th><td>${escapeHtml(ean ?? '—')}</td></tr>
          <tr><th>Kaufland-Produkt-ID</th><td>${detail.attach_id_product != null ? `<code>${escapeHtml(String(detail.attach_id_product))}</code>` : '—'}</td></tr>
          <tr><th>Kaufland-Kategorie</th><td>${escapeHtml(categoryLabel ?? (detail.kaufland_category_id != null ? `ID ${detail.kaufland_category_id}` : '—'))}</td></tr>
        </tbody>
      </table>

      ${detail.last_error ? `
        <div class="v-banner v-banner-danger klst-detail-error">
          <span class="v-icon fa-light fa-circle-xmark"></span>
          <div><strong>Letzter Fehler:</strong> ${escapeHtml(detail.last_error)}</div>
        </div>` : ''}

      ${isReadonly ? `
        <div class="v-banner v-banner-success klst-attach-info" style="margin-top:1em;">
          <span class="v-icon fa-light fa-circle-check"></span>
          <div>Angebot ist auf Kaufland aktiv. Verkaufspreis, Lagerbestand und weitere Bedingungen verwaltest Du jetzt im Tab <strong>Angebote</strong>.</div>
        </div>
      ` : `
      <h4 class="klst-detail-section">Deine Verkaufsbedingungen</h4>
      <form id="klst-attach-form" class="klst-attach-form" autocomplete="off">
        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Kondition</div>
          <div class="kl-settings-control">
            <div class="v-select" style="flex:1;">
              <select name="condition" ${isReadonly ? 'disabled' : ''}>
                ${conditionOptions.map((o) => `<option value="${o.value}" ${o.value === conditionValue ? 'selected' : ''}>${escapeHtml(o.label)}</option>`).join('')}
              </select>
            </div>
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Verkaufspreis (EUR)</div>
          <div class="kl-settings-control">
            <input type="number" step="0.01" min="0" name="listing_price" required
                   value="${listingPriceEuro}" placeholder="z.B. 19.90"
                   ${isReadonly ? 'disabled' : ''} />
            ${hintListingPrice ? `<div class="klst-attach-hint">Aus Vario: ${escapeHtml(hintListingPrice)}</div>` : ''}
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Lagerbestand</div>
          <div class="kl-settings-control">
            <input type="number" step="1" min="0" name="amount" required
                   value="${amountPrefill}" placeholder="z.B. 10"
                   ${isReadonly ? 'disabled' : ''} />
            ${hintAmount ? `<div class="klst-attach-hint">Aus Vario: ${escapeHtml(hintAmount)}</div>` : ''}
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Lieferzeit (Tage)</div>
          <div class="kl-settings-control">
            <input type="number" step="1" min="0" name="handling_time" required
                   value="${handlingTimeValue}" placeholder="z.B. 2"
                   ${isReadonly ? 'disabled' : ''} />
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Versandgruppe</div>
          <div class="kl-settings-control">
            <div class="v-select" style="flex:1;">
              <select name="id_shipping_group" required ${isReadonly ? 'disabled' : ''}>
                <option value="" ${shippingValue === '' ? 'selected' : ''}>— bitte waehlen —</option>
                ${shippingGroups.map((g) => `<option value="${escapeHtml(g.id_shipping_group)}" ${g.id_shipping_group === shippingValue ? 'selected' : ''}>${escapeHtml(g.name ?? g.id_shipping_group)}</option>`).join('')}
              </select>
            </div>
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">MwSt-Indikator</div>
          <div class="kl-settings-control">
            <div class="v-select" style="flex:1;">
              <select name="vat_indicator" required ${isReadonly ? 'disabled' : ''}>
                <option value="" ${vatValue === '' ? 'selected' : ''}>— bitte waehlen —</option>
                ${vatIndicators.map((v) => {
                  const lbl = v.label ?? v.vat_indicator;
                  const rate = v.vat_rate != null ? ` (${v.vat_rate}%)` : '';
                  return `<option value="${escapeHtml(v.vat_indicator)}" ${v.vat_indicator === vatValue ? 'selected' : ''}>${escapeHtml(lbl)}${rate}</option>`;
                }).join('')}
              </select>
            </div>
            ${vatAutoChosen ? `<div class="klst-attach-hint">Aus Vario-Steuerschema${varioResolved.vatRate != null ? ` (${varioResolved.vatRate}%)` : ''}</div>` : ''}
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Mindestpreis (EUR, optional)</div>
          <div class="kl-settings-control">
            <input type="number" step="0.01" min="0" name="minimum_price"
                   value="${minimumPriceEuro}" placeholder="leer = kein Mindestpreis"
                   ${isReadonly ? 'disabled' : ''} />
            ${hintMinimumPrice ? `<div class="klst-attach-hint">Aus Vario: ${escapeHtml(hintMinimumPrice)}</div>` : ''}
          </div>
        </div>

        <div class="kl-settings-row">
          <div class="kl-settings-row-label">Notiz (optional)</div>
          <div class="kl-settings-control">
            <textarea name="note" rows="2" placeholder="interne Notiz"
                      ${isReadonly ? 'disabled' : ''}>${escapeHtml(noteValue)}</textarea>
          </div>
        </div>

        <div class="klst-attach-footer">
          <button type="submit" class="v-button v-button-primary" id="klst-attach-submit">
            <span class="v-icon fa-regular fa-rocket"></span>
            Auf Kaufland aufschalten
          </button>
          <button type="button" class="v-button" id="klst-attach-switch-new" ${detail.status !== 'pending' ? 'disabled title="Wechsel nur im Status \'pending\' moeglich"' : ''}>
            <span class="v-icon fa-regular fa-arrow-rotate-left"></span>
            Doch neu listen
          </button>
          <div class="klst-attach-status" id="klst-attach-status"></div>
        </div>
      </form>
      `}
    </div>
  `;

  // Vario-Artikel-Link in der Pill-Bar (rechts) UND auf der SKU in der Katalog-
  // Tabelle — beide async populiert wenn die tenant-base-URL geladen ist.
  // Identisches Pattern wie im Tabs-Header (mode='new').
  const varioLink = host.querySelector<HTMLAnchorElement>('#klst-attach-vario-link');
  const skuLink = host.querySelector<HTMLAnchorElement>('#klst-attach-sku-link');
  const skuText = host.querySelector<HTMLElement>('#klst-attach-sku-text');
  if (varioLink || skuLink) {
    void getVarioBaseUrl(ctx.supabase).then((baseUrl) => {
      if (!baseUrl) return;
      const url = `${baseUrl}/products/articles/${encodeURIComponent(detail.vario_article_id)}/detail`;
      if (varioLink) {
        varioLink.href = url;
        varioLink.style.display = '';
      }
      // SKU: bevor Vario-URL da ist, zeigen wir den nackten <code>-Text; sobald
      // verfuegbar wechseln wir auf den klickbaren Link.
      if (skuLink) {
        skuLink.href = url;
        skuLink.style.display = '';
        if (skuText) skuText.style.display = 'none';
      }
    });
  }

  // ---- Submit-Handler -------------------------------------------------------
  const form = host.querySelector<HTMLFormElement>('#klst-attach-form');
  const statusEl = host.querySelector<HTMLElement>('#klst-attach-status');
  const submitBtn = host.querySelector<HTMLButtonElement>('#klst-attach-submit');
  const switchBtn = host.querySelector<HTMLButtonElement>('#klst-attach-switch-new');

  form?.addEventListener('submit', (ev) => {
    ev.preventDefault();
    void doAttachSubmit();
  });
  if (switchBtn) {
    wireTwoStepConfirm(
      switchBtn,
      `<span class="v-icon fa-regular fa-arrow-rotate-left"></span> Aufschalt-Konfiguration verwerfen?`,
      () => void doSwitchToNew(),
    );
  }

  async function doAttachSubmit(): Promise<void> {
    if (!form || !submitBtn) return;
    const fd = new FormData(form);

    const conditionRaw = (fd.get('condition') as string | null)?.trim() || null;
    const listingPriceRaw = (fd.get('listing_price') as string | null)?.trim() || '';
    const amountRaw = (fd.get('amount') as string | null)?.trim() || '';
    const handlingTimeRaw = (fd.get('handling_time') as string | null)?.trim() || '';
    const idShippingGroupRaw = (fd.get('id_shipping_group') as string | null)?.trim() || null;
    const vatIndicatorRaw = (fd.get('vat_indicator') as string | null)?.trim() || null;
    const minimumPriceRaw = (fd.get('minimum_price') as string | null)?.trim() || '';
    const noteRaw = (fd.get('note') as string | null)?.trim() || '';

    // Validierung
    const listingPriceNum = Number(listingPriceRaw);
    const amountNum = Number(amountRaw);
    const handlingTimeNum = Number(handlingTimeRaw);
    if (!conditionRaw) { setStatus('Kondition fehlt.', 'error'); return; }
    if (!Number.isFinite(listingPriceNum) || listingPriceNum <= 0) { setStatus('Verkaufspreis fehlt oder ungueltig.', 'error'); return; }
    if (!Number.isFinite(amountNum) || amountNum < 0 || !Number.isInteger(amountNum)) { setStatus('Lagerbestand fehlt oder ungueltig.', 'error'); return; }
    if (!Number.isFinite(handlingTimeNum) || handlingTimeNum < 0 || !Number.isInteger(handlingTimeNum)) { setStatus('Lieferzeit fehlt oder ungueltig.', 'error'); return; }
    if (!idShippingGroupRaw) { setStatus('Versandgruppe fehlt.', 'error'); return; }
    if (!vatIndicatorRaw) { setStatus('MwSt-Indikator fehlt.', 'error'); return; }

    let minimumPriceCentsOrNull: number | null = null;
    if (minimumPriceRaw.length > 0) {
      const n = Number(minimumPriceRaw);
      if (!Number.isFinite(n) || n <= 0) { setStatus('Mindestpreis ungueltig.', 'error'); return; }
      minimumPriceCentsOrNull = Math.round(n * 100);
    }

    // STRIKT snake-case Keys — Worker T7 (apps/worker/src/jobs/kaufland-publish-listing.ts)
    // liest exakt: condition, listing_price (cents), amount, handling_time,
    // id_shipping_group, vat_indicator, minimum_price (cents), note.
    const overrides: Record<string, unknown> = {
      condition: conditionRaw,
      listing_price: Math.round(listingPriceNum * 100),
      amount: amountNum,
      handling_time: handlingTimeNum,
      id_shipping_group: idShippingGroupRaw,
      vat_indicator: vatIndicatorRaw,
      minimum_price: minimumPriceCentsOrNull,
      note: noteRaw.length > 0 ? noteRaw : null,
    };

    submitBtn.disabled = true;
    setStatus('Speichere Overrides…', 'info');

    // 1. offerOverrides via security-definer-RPC schreiben. Direkt-UPDATE auf
    //    kaufland.listings ist authenticated-Rolle nicht erlaubt (nur SELECT-Grant);
    //    die RPC kapselt das Snapshot-Merge inkl. status='pending'-Vorbedingung.
    const sav = await ctx.supabase.rpc('kaufland_save_attach_overrides', {
      _listing_id: detail.id,
      _overrides: overrides,
    });
    if (sav.error) {
      setStatus(`Speichern fehlgeschlagen: ${sav.error.message}`, 'error');
      submitBtn.disabled = false;
      return;
    }

    // 2. Job enqueuen
    setStatus('Stelle Listing in die Push-Queue…', 'info');
    const enq = await ctx.supabase.rpc('kaufland_enqueue_listing_publish', { _listing_id: detail.id });
    if (enq.error) {
      setStatus(`Queue-Fehler: ${enq.error.message}`, 'error');
      submitBtn.disabled = false;
      return;
    }

    showToast('Listing zur Aufschaltung eingestellt.', 'success');
    setStatus('Push laeuft – warte auf Kaufland …', 'info');
    // Statt onReload() (das die Inbox neu laedt und das Detail via Loading-
    // Spinner ueberschreibt): in-place Status-Polling. Pollt alle 2s den
    // Listing-Status + last_error und aktualisiert NUR die Pill-Bar und das
    // Error-Banner im DOM. Stoppt bei published/error oder nach ~40s.
    pollStatusUntilTerminal();
  }

  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  function stopPolling(): void {
    if (pollTimer !== null) { clearTimeout(pollTimer); pollTimer = null; }
  }
  function pollStatusUntilTerminal(): void {
    stopPolling();
    let attempts = 0;
    const MAX = 20; // 20 * 2s = 40s
    const tick = async (): Promise<void> => {
      // Host wurde ersetzt/entfernt (Re-Mount im Slot oder Lister-Reload) —
      // Polling beenden statt ins tote DOM zu schreiben (Review-Befund Task 4).
      if (!host.isConnected) { stopPolling(); return; }
      // innerHTML-Re-Mount: Container existiert noch, aber unser Form-DOM ist weg.
      if (form && !form.isConnected) { stopPolling(); return; }
      attempts++;
      const { data, error: pollErr } = await ctx.supabase.rpc('kaufland_get_listing_detail', { _id: detail.id });
      if (pollErr) {
        console.warn('[listing-detail-pane attach-poll] failed:', pollErr.message);
        if (attempts < MAX) pollTimer = setTimeout(() => void tick(), 2000);
        return;
      }
      const fresh = (Array.isArray(data) ? data[0] : data) as ListingDetail | null | undefined;
      if (!fresh) return;
      applyLiveStatus(host, fresh);
      if (fresh.status === 'published' || fresh.status === 'error') {
        // Terminal: stoppen + Inbox einmalig refreshen damit dortige Pill
        // (queued -> published/error) konsistent ist.
        pollTimer = null;
        onReload();
        return;
      }
      if (attempts < MAX) pollTimer = setTimeout(() => void tick(), 2000);
    };
    void tick();
  }

  async function doSwitchToNew(): Promise<void> {
    stopPolling();
    const { error: switchErr } = await ctx.supabase.rpc('kaufland_switch_listing_to_new', { _listing_id: detail.id });
    if (switchErr) {
      setStatus(`Wechsel fehlgeschlagen: ${switchErr.message}`, 'error');
      return;
    }
    showToast('Modus auf "Neu listen" gewechselt.', 'success');
    onReload();
  }

  function setStatus(msg: string, kind: 'info' | 'error'): void {
    if (!statusEl) return;
    statusEl.textContent = msg;
    statusEl.className = `klst-attach-status klst-attach-status--${kind}`;
  }
}

/** Aktualisiert die Status-Pill und das last_error-Banner im offenen Aufschalt-
 *  Panel, ohne das ganze DOM neu zu rendern. Wird vom Polling-Loop nach Submit
 *  benutzt, damit der User Status-Uebergaenge (pending → queued → published/
 *  error) live sieht ohne dass das Form verschwindet. */
function applyLiveStatus(host: HTMLElement, fresh: ListingDetail): void {
  // 1. Status-Pill in der Header-Pill-Bar austauschen (zweites .v-pill nach
  //    der "Aufschalten"-Pill).
  const pillBar = host.querySelector<HTMLElement>('.klst-attach-header-pills');
  if (pillBar) {
    const pills = pillBar.querySelectorAll<HTMLElement>('.v-pill');
    const statusPillEl = pills[1]; // 0=Aufschalten, 1=Status, 2=Storefront
    if (statusPillEl) {
      const pill = STATUS_PILL[fresh.status] ?? STATUS_PILL.pending;
      statusPillEl.className = `v-pill ${pill.cls}`;
      statusPillEl.textContent = pill.label;
    }
  }

  // 2. last_error-Banner: existing entfernen wenn keine Message, sonst einfuegen/
  //    aktualisieren ueber der Form (unter der Katalog-Tabelle).
  const existingErrBanner = host.querySelector<HTMLElement>('.klst-detail-error');
  if (fresh.last_error) {
    const escapedErr = fresh.last_error
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    if (existingErrBanner) {
      const msgEl = existingErrBanner.querySelector('div');
      if (msgEl) msgEl.innerHTML = `<strong>Letzter Fehler:</strong> ${escapedErr}`;
    } else {
      const formEl = host.querySelector<HTMLElement>('.klst-attach-form');
      if (formEl) {
        const banner = document.createElement('div');
        banner.className = 'v-banner v-banner-danger klst-detail-error';
        banner.innerHTML = `<span class="v-icon fa-light fa-circle-xmark"></span>
          <div><strong>Letzter Fehler:</strong> ${escapedErr}</div>`;
        formEl.parentNode?.insertBefore(banner, formEl);
      }
    }
  } else if (existingErrBanner) {
    existingErrBanner.remove();
  }

  // 3. Status-Footer-Text und Submit-Button entsprechend setzen.
  const statusEl = host.querySelector<HTMLElement>('#klst-attach-status');
  const submitBtn = host.querySelector<HTMLButtonElement>('#klst-attach-submit');
  if (fresh.status === 'published') {
    if (statusEl) {
      statusEl.textContent = 'Erfolgreich auf Kaufland aufgeschaltet.';
      statusEl.className = 'klst-attach-status klst-attach-status--info';
    }
    if (submitBtn) submitBtn.disabled = true;
  } else if (fresh.status === 'error') {
    if (statusEl) {
      statusEl.textContent = 'Fehler beim Push – siehe Banner. Felder ggf. anpassen und erneut "Auf Kaufland aufschalten" druecken.';
      statusEl.className = 'klst-attach-status klst-attach-status--error';
    }
    if (submitBtn) submitBtn.disabled = false;
  }
}

function pickStringOverride(obj: Record<string, unknown>, key: string): string | null {
  const v = obj[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function pickNumberOverride(obj: Record<string, unknown>, key: string): number | null {
  const v = obj[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return null;
}
